import { useEffect, useState } from 'react';
import { leaveAmountText } from '../../lib/leave-amount';
import { leaveTimeRangeText } from '../../lib/partial-leave-time';
// 화면 머리 — 근태 · 메일과 같은 정본 A3 (2026-09-08).
import { PageHeader } from '../../components/shared/PageHeader';
import { getTodayAttendance, getAttendanceSummary, type TodayAttendance, type AttendanceSummaryDto } from '../../api/attendance';
import { getLeaveSummary, listLeaves, type LeaveSummaryDto, type LeaveDto } from '../../api/hr';
import { leaveAvailable } from '../../lib/leave-balance';
// 쪽 머리 경로 · 범위 칩은 메뉴 배치에서(2026-10-17 · web 레인 W7a — lib/page-crumbs.ts)
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';

/**
 * 내 인사 홈 — 직원이 인사 메뉴를 눌렀을 때 처음 보는 화면.
 *
 * 담당자 홈(HrHomePage)과 **같은 뼈대에 범위만 나**로 좁힌 것이다. 여기도 새 API 는 없다 —
 * 내 근태가 이미 부르는 셋(오늘 기록 · 이달 요약 · 연차 요약)과 휴가 신청 내역을 그대로 쓴다.
 *
 * ★ 출퇴근 **찍기는 여기서 하지 않는다.** 그것은 「내 근태」의 일이고, 두 화면이 같은 버튼을
 *   가지면 "어디서 찍었더라" 가 생긴다. 여기서는 오늘 상태를 **읽고** 그 화면으로 보낸다.
 * ★ 못 읽으면 그 절만 접는다(옛 서버·권한 어느 쪽이든) — 바로가기는 남는다.
 */
const HHMM = (iso: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const LEAVE_STATUS_LABEL: Record<string, string> = {
  APPROVED: '승인', PENDING: '대기', REJECTED: '반려',
};

export function MyHrHomePage() {
  const [today, setToday] = useState<TodayAttendance | null>(null);
  const [month, setMonth] = useState<AttendanceSummaryDto | null>(null);
  const [leave, setLeave] = useState<LeaveSummaryDto | null>(null);
  const [leaves, setLeaves] = useState<LeaveDto[]>([]);
  const [state, setState] = useState<'loading' | 'ok' | 'folded'>('loading');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [t, m, l] = await Promise.all([
          getTodayAttendance(), getAttendanceSummary(), getLeaveSummary(),
        ]);
        if (!alive) return;
        setToday(t); setMonth(m); setLeave(l);
        setState('ok');
        // 신청 내역은 없어도 화면이 선다 — 따로 삼킨다.
        try {
          const rows = await listLeaves();
          if (alive) setLeaves(rows);
        } catch { /* 목록만 비워 둔다 */ }
      } catch {
        if (alive) setState('folded');
      }
    })();
    return () => { alive = false; };
  }, []);

  const checkIn = HHMM(today?.checkInAt ?? null);
  const checkOut = HHMM(today?.checkOutAt ?? null);
  const pending = leaves.filter((l) => l.status === 'PENDING').length;

  return (
    <div className="hrpage">
      {/*
        ★★★ 화면 머리 — **정본 `PageHeader`(A3)** (2026-09-08 사용자 지적).
           손으로 짠 `<h1>` + `<p>` 라 **경로 · 도움말**이 없었고, 경로를 «글자»로
           설명문 앞에 적고 있었다 — 경로는 `breadcrumb` 이 그린다.
        ★★ **경로의 마지막은 «지금 화면»**(A3 · 2026-09-08 사용자 지시로 메일에서 못 박은 규칙).
           처음엔 옛 글자를 그대로 옮겨 「업무 › 인사」로 뒀는데, 그러면 **인사만 다른 모양**이 된다.
        ★ 제목은 **메뉴 이름과 같아야 한다** — 이 주소는 보는 사람에 따라 갈리므로
          메뉴도 둘(«인사 홈» · «내 인사 홈»)이고, 제목도 그 둘을 그대로 쓴다.
      */}
      <PageHeader
        className="hrpage__head"
        breadcrumb={crumbsFor('/erp/hr', { view: 'staff' })}
        scope={scopeFor('/erp/hr', { view: 'staff' })}
        title="내 인사 홈"
        description='내 근태와 휴가, 내가 올린 신청을 한 곳에서 봅니다'
        help={{
          title: '이 화면의 규칙',
          body: (
            <>
              <p><b>남은 연차</b>는 승인된 것까지 뺀 값입니다 — 신청 중인 것은 아직 안 빠집니다.</p>
              <p>신청서는 <b>근태</b>의 「신청서 작성」에서 냅니다. 낸 뒤의 상태는 「내 근태 내역」에서 봅니다.</p>
              <p>내 인사기록이 틀렸으면 <b>인사 담당자</b>에게 알려 주세요 — 본인이 고치는 화면은 없습니다.</p>
            </>
          ),
        }}
      />

      {state === 'loading' && <div className="hrpage__note">불러오는 중…</div>}

      {state === 'ok' && (
        <>
          <div className="hrpage__kpis">
            <div className="hrpage__kpi is-acc">
              <span className="hrpage__kpil">오늘</span>
              <span className="hrpage__kpiv">
                {checkIn ?? '—'}
                <small>{checkOut ? `→ ${checkOut} 퇴근` : (checkIn ? '근무 중' : '출근 전')}</small>
              </span>
              <span className="hrpage__kpid">
                {today?.deemed ? `간주 출근 · ${today.deemed.destination}` : '출퇴근은 내 근태에서 찍습니다'}
              </span>
            </div>
            <div className="hrpage__kpi">
              <span className="hrpage__kpil">연차 잔여</span>
              <span className="hrpage__kpiv">
                {leaveAvailable(leave?.annual) ?? 0}<small>일</small>
              </span>
              <span className="hrpage__kpid">
                총 {leave?.annual.total ?? '—'}일 · 사용 {leave?.annual.used ?? 0}일
              </span>
            </div>
            <div className="hrpage__kpi">
              <span className="hrpage__kpil">이달 근무</span>
              <span className="hrpage__kpiv">{month?.workedDays ?? 0}<small>일</small></span>
              <span className="hrpage__kpid">휴가 {month?.leaveDays ?? 0}일</span>
            </div>
            <div className={`hrpage__kpi${(month?.lateDays ?? 0) > 0 ? ' is-warn' : ''}`}>
              <span className="hrpage__kpil">이달 지각</span>
              <span className="hrpage__kpiv">{month?.lateDays ?? 0}<small>회</small></span>
              <span className="hrpage__kpid">기준 {month?.lateThreshold ?? '09:00'}</span>
            </div>
            <div className={`hrpage__kpi${pending > 0 ? ' is-warn' : ''}`}>
              <span className="hrpage__kpil">결재 진행</span>
              <span className="hrpage__kpiv">{pending}<small>건</small></span>
              <span className="hrpage__kpid">
                {leave?.annual.pending ? `신청 중 ${leave.annual.pending}일` : '대기 중인 신청 없음'}
              </span>
            </div>
          </div>

          <section className="hrpage__panel">
            <h2 className="hrpage__panelt">
              내 신청 상태
              <span className="hrpage__panels">최근 5건</span>
            </h2>
            {leaves.length === 0 ? (
              <p className="hrpage__empty">아직 신청 내역이 없습니다.</p>
            ) : (
              <table className="hrpage__tbl">
                <tbody>
                  {leaves.slice(0, 5).map((l) => (
                    <tr key={l.id}>
                      <td>
                        <b>{l.startDate}</b>
                        {l.endDate !== l.startDate ? <span className="hrpage__dim"> ~ {l.endDate}</span> : null}
                        {leaveTimeRangeText(l) ? <span className="hrpage__dim"> {leaveTimeRangeText(l)}</span> : null}
                        <span className="hrpage__dim"> · {l.reason || '사유 없음'}</span>
                      </td>
                      <td className="hrpage__num">{leaveAmountText(l, leave?.dailyMinutes)}</td>
                      <td className="hrpage__act">{LEAVE_STATUS_LABEL[l.status] ?? l.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}

      {state === 'folded' && (
        <div className="hrpage__note">
          지금 내 현황을 불러오지 못했습니다 — 왼쪽 메뉴로는 그대로 들어갈 수 있습니다.
        </div>
      )}

      {/*
        ★ 「바로가기」 절을 걷어냈다 (2026-08-29 · 사용자 지적 — 「인사 하위의 바로가기는 다」).
          담당자 홈과 같은 이유다: 왼쪽에 트리가 서 있는데 같은 목록을 카드로 한 번 더 그리면,
          홈에서 할 수 있는 일이 «메뉴를 다시 보여 주는 것» 뿐이 된다.
      */}
    </div>
  );
}
